import FormControl from 'Design/components/FormControl'
import React, { Fragment } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import cx from 'classnames'
import { convertToSectionObject } from './bot-response-setting-helper'
import styles from './KnowledgeBaseConfigTab.module.scss'
import SwitchInput from 'Design/components/SwitchInput'
import TagsInput from 'Components/TagsInput'
import CheckboxTree, { toTree } from 'Design/components/CheckboxTree'
import Tips, { Tip } from 'Src/b/pages/protected/Content/components/Tips'

const KnowledgeBaseConfigTab = ({ allKBSections, allSections }) => {
  const {
    watch,
    getValues,
    setValue,
    trigger,
    control,
    formState: { errors }
  } = useFormContext()

  const enableKBFilter = watch('enableKBFilter', getValues('enableKBFilter'))
  const enableTagsFilter = watch('enableTagsFilter', getValues('enableTagsFilter'))
  const selectedSections = watch('selectedSections', getValues('selectedSections'))
  const selectedTags = watch('selectedTags', getValues('selectedTags'))

  return (
    <div className={styles.KBTagsSection}>
      <div className={styles.KBSection}>
        <FormControl className={cx(styles.EnableKBFilter, 'ml-form-control')}>
          <Controller
            control={control}
            name='enableKBFilter'
            render={({ field: { value, onChange } }) => {
              return (
                <Fragment>
                  <SwitchInput
                    checked={value}
                    onChange={(isEnabled) => {
                      const sections = [].concat(...Object.values(selectedSections).filter((arr) => !!arr))
                      if (!sections?.length) {
                        setValue('selectedSections', convertToSectionObject(allSections))
                      }
                      onChange(isEnabled)
                    }}
                  >
                    Lookup content only from selected sections
                  </SwitchInput>
                </Fragment>
              )
            }}
          />
        </FormControl>
        {enableKBFilter && allKBSections?.length > 0 && (
          <Fragment>
            <div className={cx(styles.SectionContainer, { [styles.ErrorBox]: errors?.selectedSections })}>
              {allKBSections &&
                allKBSections.map((kb, i) => {
                  return (
                    kb.sections.length > 0 && (
                      <Fragment key={kb.id}>
                        <Controller
                          control={control}
                          name={`selectedSections[${kb.id.toString()}]`}
                          render={({ field: { value, onChange } }) => {
                            return (
                              <Fragment>
                                <label className={styles.KBNames}>{kb.name}</label>
                                <SectionsTree
                                  key={kb.id}
                                  sections={kb.sections}
                                  selectedKBSections={value || []}
                                  onChange={(sections) => {
                                    onChange(sections)
                                    trigger('selectedSections')
                                  }}
                                />
                              </Fragment>
                            )
                          }}
                        />
                      </Fragment>
                    )
                  )
                })}
            </div>
            {errors?.selectedSections && (
              <small className='form-text text-danger'>{errors.selectedSections?.message}</small>
            )}
          </Fragment>
        )}
        {enableKBFilter && !allKBSections?.length && (
          <div className={styles.EmptyKB}>Please add Knowledge Base Articles to fine tune your Chatbot!</div>
        )}
      </div>

      <FormControl className={cx(styles.EnableKBFilter, 'ml-form-control')}>
        <Controller
          control={control}
          name='enableTagsFilter'
          render={({ field: { value, onChange } }) => {
            return (
              <SwitchInput checked={value} onChange={onChange}>
                Lookup content only containing specific tags
              </SwitchInput>
            )
          }}
        />
      </FormControl>
      {enableTagsFilter && (
        <Controller
          control={control}
          name='selectedTags'
          render={({ field: { value, onChange } }) => {
            return (
              <div className={styles.TagsInput}>
                <TagsInput
                  hasError={errors?.selectedTags}
                  value={value.map((tag) => ({
                    label: tag,
                    value: tag
                  }))}
                  onChange={(tags) => onChange(tags.map((tag) => tag.value))}
                />
              </div>
            )
          }}
        />
      )}
      {enableTagsFilter && errors?.selectedTags && (
        <small className='form-text text-danger'>{errors.selectedTags?.message}</small>
      )}
      <div className={styles.Result}>
        <KBTagsFilterResult
          hasIgnoredSections={allKBSections?.some((kb) => kb.ignored_sections.length > 0)}
          sections={selectedSections}
          tags={selectedTags}
          enableKBFilter={enableKBFilter}
          enableTagsFilter={enableTagsFilter}
        />
      </div>
    </div>
  )
}

const SectionsTree = ({ sections, selectedKBSections, onChange }) => {
  const nodes = toTree(sections)

  return <CheckboxTree nodes={nodes} checked={selectedKBSections} onChange={onChange} />
}

const KBTagsFilterResult = ({ hasIgnoredSections, sections, tags, enableKBFilter, enableTagsFilter }) => {
  const userInfo = () => {
    if (sections.length && tags.length && enableKBFilter && enableTagsFilter) {
      return `The Chatbot will only provide responses based on the Knowledge Base articles
    present in the selected sections and containing the tag(s): ${tags.toString()}.`
    }
    if (sections.length && enableKBFilter) {
      return `The Chatbot will only provide responses based on the Knowledge Base articles
    present in the selected sections.`
    }
    if (tags.length && enableTagsFilter) {
      return `The Chatbot will only provide responses based on the Knowledge Base articles containing the tag(s): ${tags.toString()}.`
    }
    return `Enhance your Chatbot's accuracy by training it with relevant Content. Select KnowledgeBase sections and Tags for the chatbot to lookup accurate responses`
  }

  return (
    <Tips title='How does it work?'>
      <Tip>{userInfo()}</Tip>
      {hasIgnoredSections && (
        <Tip>
          Selecting sections here will override any ignored sections configured in Content &gt; Knowledge Base
          configuration
        </Tip>
      )}
    </Tips>
  )
}

export default KnowledgeBaseConfigTab
